import React, { useState } from "react";
import { getAuth, setPersistence, browserSessionPersistence, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { ShoppingCart } from "lucide-react";
import Login from "./Login";
import Register from "./Register";
import MartCarousel from "./components/MartCarousel";
import ForgotPassword from "./ForgotPassword";

function Welcome({ onStartGuest, onLoginSuccess }) {
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  //google login logic
  const handleGoogleLogin = async() => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try{

      //set persistance to session (clears when tab closes)
      await setPersistence(auth, browserSessionPersistence);

      //open the popup
      await signInWithPopup(auth, provider);

      //login successful
      setShowEntryModal(false);
      onLoginSuccess();

    } catch(error){
      console.error("Google Sign-In Error:", error);
      alert("Google Sign-In failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFEFD5] via-[#FFE2BC] to-[#D6C7B0] flex flex-col">

      {/* ================= HEADER ================= */}
      <header className="px-6 sm:px-12 py-6 flex items-center">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-[#FFE6C7] flex items-center justify-center shadow">
            <ShoppingCart className="w-6 h-6 text-[#FF8C42]" />
          </div>
          <span className="text-xl sm:text-2xl font-extrabold text-[#5A3E2B]">
            Intelligent Budget System
          </span>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <main className="flex-1 flex justify-center pt-16 pb-10">

        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center gap-12">

          {/* TEXT */}
          <div className="w-full md:w-1/2 text-center md:text-left space-y-6">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#5A3E2B] leading-tight">
              Your Budget. Your Choices.
              <span className="text-[#FF8C42]"> Optimized.</span>
            </h1>

            <p className="text-base sm:text-lg text-[#6F5A42] max-w-lg mx-auto md:mx-0">
              Let AI optimize your grocery selections for maximum value.
              Compare prices, stay within budget, and shop smarter with confidence.
            </p>

            <button
              onClick={() => setShowEntryModal(true)}
              className="w-full sm:w-auto px-8 py-4 mt-4 text-base sm:text-lg font-bold rounded-xl
                         bg-[#FF8C00] text-white
                         shadow-[0_4px_20px_rgba(255,140,0,0.45)]
                         hover:brightness-110 transition"
            >
              Let’s Get Started →
            </button>
          </div>

          {/* IMAGE */}
          <div className="w-full md:w-1/2 flex justify-center">
            <img
              src="/WELCOME.png"
              alt="Welcome"
              className="w-[260px] sm:w-[360px] lg:w-[580px]
                         drop-shadow-[0_25px_40px_rgba(0,0,0,0.35)]
                         pointer-events-none"
            />
          </div>
        </div>
      </main>

      {/* ================= FOOTER ================= */}
<footer className="mt-auto relative z-10">
  <MartCarousel />
</footer>

      {/* ================= ENTRY MODAL ================= */}
      {showEntryModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[50]">
          <div className="bg-white rounded-2xl p-6 sm:p-10 w-full max-w-md mx-4 shadow-2xl text-center space-y-6">
            <h2 className="text-2xl font-bold text-[#5A3E2B]">
              How would you like to continue?
            </h2>

            <button
              onClick={() => {
                setShowEntryModal(false);
                setShowLogin(true); //open new firebase login
              }}
              className="w-full py-3 rounded-xl bg-[#FF8C42] text-white font-bold hover:brightness-110 transition"
            >
              Login
            </button>

            <button
              onClick={() => {
                setShowEntryModal(false);
                setShowRegister(true); // open new firebase register
              }}
              className="w-full py-3 rounded-xl bg-[#FFB67A] text-[#5A3E2B] font-bold hover:bg-[#ffac6a] transition"
            >
              Sign Up
            </button>

            <button
              onClick={() => {
                setShowEntryModal(false);
                onStartGuest();
              }}
              className="w-full py-3 rounded-xl bg-gray-200 font-semibold hover:bg-gray-300-transition"
            >
              Continue as Guest
            </button>

            {/*gray line */}
            <div className="flex items-center my-6">
              {/* The Left Line */}
              <div className="flex-grow border-t-2 border-gray-300"></div>

              {/* The Word */}
              <span className="mx-4 text-gray-500 font-medium">or</span>

              {/* The Right Line */}
              <div className="flex-grow border-t-2 border-gray-300"></div>
            </div>  

            {/*Google Sign-In Button*/}
            <button
              onClick={handleGoogleLogin}
              className="w-full py-3 rounded-xl bg-white border-2 border-gray-200 text-gray-700 font-bold 
                         flex items-center justify-center gap-3 hover:bg-gray-50 transition"
            >
              <img
                src="https://www.svgrepo.com/show/475656/google-color.svg"
                alt="Google"
                className="w-5 h-5"
              />
              Login with Google
            </button>

            <button
              onClick={() => setShowEntryModal(false)}
              className="text-sm text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Render Firebase */}
      {showLogin && (
        <Login
          onLogin={onLoginSuccess}
          onBack={() => setShowLogin(false)}
          onForgotPassword={()=>{
            setShowLogin(false);
            setShowForgotPassword(true);
          }}
        />
      )}

      {showRegister && (
        <Register
          onRegisterSuccess={onLoginSuccess}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }} 
          onBack={() => setShowRegister(false)}
        />
      )}

      {/*Render the forgot password*/}
      {showForgotPassword && (
        <ForgotPassword 
          onBack={()=>{
            setShowForgotPassword(false);
            setShowLogin(true);
          }}
        />
      )}
    </div>
  );
}

export default Welcome;
